/**
 * Scripted content for Demo Mode.
 *
 * Every answer here is INVENTED for a fictional student so that PAILA can be
 * shown to an external audience without touching live university systems or
 * real student data. Module codes are UEL's public Computer Science catalogue
 * codes; marks, rooms, times, names and dates are made up.
 *
 * Answers are written in the same voice and layout as PAILA's live replies
 * (see docs/PAILA_Demo_Run_Sheet.md for verbatim live examples).
 */

export type DemoGroup = 'timetable' | 'record' | 'campus' | 'about';

export interface DemoEntry {
  id: string;
  group: DemoGroup;
  /** Short label shown on the welcome-screen chip. */
  chipLabel: string;
  /** The canonical question. Chips insert this text into the composer. */
  question: string;
  /** Alternative phrasings that should resolve to the same answer. */
  aliases: string[];
  /** "Thinking" status lines shown in sequence before the answer streams. */
  statuses: string[];
  /** Markdown answer. */
  answer: string;
  /** Show the red crisis banner + red bubble, as the live system does. */
  isCrisis?: boolean;
  /** Guardrail refusal: answered almost instantly, no tool statuses. */
  isRefusal?: boolean;
}

export const DEMO_STUDENT = {
  name: 'Amara Okafor',
  firstName: 'Amara',
  studentId: 'u2400123',
  programme: 'BSc (Hons) Computer Science',
  year: 'Year 2 (Level 5)',
  campus: 'Docklands',
};

export function normaliseQuestion(text: string): string {
  return text
    .toLowerCase()
    .replace(/[’‘`]/g, "'")
    .replace(/[^a-z0-9' ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export const DEMO_ENTRIES: DemoEntry[] = [
  {
    id: 'greeting',
    group: 'about',
    chipLabel: 'Say hello',
    question: 'Hi',
    aliases: ['Hello', 'Hi PAILA', 'Hello PAILA', 'Hey'],
    statuses: [],
    answer: `Hello ${DEMO_STUDENT.firstName}! I'm PAILA, your Personal AI Learning Assistant for UEL. I'm here to help you with information about your classes, assignments, grades, and university services.

How can I assist you today? I can help with:
- Your class schedule and timetable
- Assignment deadlines
- Module grades and results
- Attendance records
- General university information and policies

What would you like to know about?`,
  },
  {
    id: 'today',
    group: 'timetable',
    chipLabel: "Today's classes",
    question: 'What classes do I have today?',
    aliases: ["What's my schedule today?", 'What have I got on today?', 'What is my timetable today?'],
    statuses: ['Checking your timetable in CELCAT…'],
    answer: `Here's your timetable for today, ${DEMO_STUDENT.firstName}:

**10:00–12:00 — CD5000 Database Systems** (Lecture)
- 📍 Room RD.2.25, Docklands Campus
- 👤 Dr Priya Raman

**14:00–16:00 — CD5004 Advanced Programming** (Workshop)
- 📍 Room USG.2.01, University Square Stratford
- 👤 Mr Daniel Adeyemi

You have a two-hour gap between sessions — enough time to travel from Docklands to Stratford (about 15 minutes on the DLR and Jubilee line) and grab lunch.

Would you like directions to either room, or to see the rest of your week?`,
  },
  {
    id: 'week',
    group: 'timetable',
    chipLabel: 'This week',
    question: 'Show me my timetable for this week',
    aliases: ["What's my timetable this week?", 'What classes do I have this week?', 'Show my timetable this week'],
    statuses: ['Checking your timetable in CELCAT…', 'Collecting this week\'s events…'],
    answer: `Here's your week at a glance:

**Monday**
- 10:00–12:00 — CD5000 Database Systems (Lecture) — RD.2.25, Docklands
- 14:00–16:00 — CD5004 Advanced Programming (Workshop) — USG.2.01, Stratford

**Tuesday**
- 09:00–11:00 — CD5002 Data Communications and Networks (Lecture) — RD.1.11, Docklands
- 13:00–15:00 — CD5010 Cyber Security (Lab) — EB.2.42, Docklands

**Wednesday**
- No timetabled classes — independent study day

**Thursday**
- 11:00–13:00 — CD5007 Business Change (Seminar) — USG.3.14, Stratford
- 15:00–17:00 — CD5012 Mental Wealth: Professional Practice 2 (Workshop) — USG.1.05, Stratford

**Friday**
- 10:00–12:00 — CD5004 Advanced Programming (Lab) — EB.G.14, Docklands

That's 7 sessions and 14 timetabled hours this week. Is there a particular day you'd like more detail on?`,
  },
  {
    id: 'next-class',
    group: 'timetable',
    chipLabel: 'Next class',
    question: 'When is my next class?',
    aliases: ["When's my next class?", 'Where is my next class?', 'What is my next class?'],
    statuses: ['Checking your timetable in CELCAT…'],
    answer: `Your next class is:

**CD5000 Database Systems** (Lecture)
- 🕙 Today, 10:00–12:00
- 📍 Room RD.2.25, Royal Docks Centre for Sustainability, Docklands Campus
- 👤 Dr Priya Raman

**Getting there:** RD.2.25 is on the second floor of the Royal Docks Centre. From Cyprus DLR station, walk through the main campus square; the building is on your left, about three minutes away.

Would you like me to remind you what was covered in last week's session, or check what's due for this module?`,
  },
  {
    id: 'grades',
    group: 'record',
    chipLabel: 'Module grades',
    question: 'What are my module grades?',
    aliases: ['Show me my grades', 'What are my grades?', 'What are my results?', 'Show me my module results'],
    statuses: ['Retrieving your academic record from SITS…', 'Formatting your results…'],
    answer: `Here are your module grades, ${DEMO_STUDENT.firstName}:

**2025/6 Academic Year (Level 4) — complete**
- **CD4004 Maths for Computing**: 88% (Pass) — 20 credits
- **CD4001 Software Development**: 81% (Pass) — 20 credits
- **CD4002 Computer Systems and Networks**: 76% (Pass) — 20 credits
- **CD4003 Web Technologies**: 79% (Pass) — 20 credits
- **CD4000 Information Systems Modelling and Design**: 72% (Pass) — 20 credits
- **CD4005 Professional Practice 1 (Mental Wealth)**: 84% (Pass) — 20 credits

**2026/7 Academic Year (Level 5) — in progress**
- CD5000 Database Systems, CD5002 Data Communications and Networks, CD5004 Advanced Programming, CD5007 Business Change, CD5010 Cyber Security, CD5012 Mental Wealth: Professional Practice 2 — no marks released yet.

Congratulations on passing all 120 credits at Level 4 with an average of 80% — that's a strong start. Would you like to see how these results feed into your progression?`,
  },
  {
    id: 'progression',
    group: 'record',
    chipLabel: 'Am I on track?',
    question: 'Am I on track to progress?',
    aliases: ['What is my progression status?', 'What is my enrolment status?', 'Am I on track?'],
    statuses: ['Retrieving your progression record from SITS…'],
    answer: `Good news — you're on track.

- **Progression status:** Progressed to Level 5 (Year 2) for 2026/7
- **Enrolment status:** Currently Enrolled — ${DEMO_STUDENT.programme}
- **Credits achieved:** 120 of 120 at Level 4
- **Account:** No holds, no outstanding balance (£0.00)

To progress to Level 6 next year you'll need to pass all 120 Level 5 credits. Your Level 4 average of 80% would count towards a First if you keep up that standard.

Would you like a summary of what's due this term, or details of any module?`,
  },
  {
    id: 'assignments',
    group: 'record',
    chipLabel: 'Assignments due',
    question: 'What assignments do I have due?',
    aliases: ['What assignments are due soon?', 'What assignments are due?', 'What is due soon?'],
    statuses: ['Checking your Moodle courses…', 'Collecting upcoming deadlines…'],
    answer: `Here are your upcoming assignments from Moodle:

**Due in the next 4 weeks**
1. **CD5000 Database Systems — Coursework 1: ER Modelling & Normalisation** — due Friday 9 October, 16:00 · Individual · 30% of module
2. **CD5004 Advanced Programming — Lab Portfolio (Part 1)** — due Monday 19 October, 12:00 · Individual · 20% of module
3. **CD5007 Business Change — Group Work Submission: Change Management Theories and Models** — due Thursday 29 October, 16:00 · Group · Formative

**Later this term**

- **CD5010 Cyber Security — Threat Modelling Report** — due Friday 20 November
- **CD5002 Data Communications and Networks — Network Design Assignment** — due Friday 4 December

Your nearest deadline is the Database Systems coursework in just over a week. Would you like the marking criteria or the reading list for it?`,
  },
  {
    id: 'attendance',
    group: 'record',
    chipLabel: 'Attendance in March',
    question: 'What was my attendance like in March?',
    aliases: ['What was my attendance in March?', 'Show me my attendance for March', 'How was my attendance in March?'],
    statuses: [
      'Checking your timetable in CELCAT…',
      'Matching classes against Lenel swipe-card records…',
      'Calculating attendance…',
    ],
    answer: `Here's your attendance for **March 2026**:

**Overall: 91%** — you attended 21 of 23 timetabled classes.

**By module**
- CD4004 Maths for Computing — 100% (4/4)
- CD4001 Software Development — 100% (4/4)
- CD4002 Computer Systems and Networks — 100% (4/4)
- CD4003 Web Technologies — 75% (3/4) ⚠️
- CD4000 Information Systems Modelling and Design — 75% (3/4) ⚠️
- CD4005 Professional Practice 1 — 100% (3/3)

**Class-by-class log (sample)**
- Mon 2 Mar — CD4001 Workshop, EB.G.14 — ✅ ATTENDED — swipe at 09:52
- Tue 3 Mar — CD4003 Lecture, RD.1.11 — ✅ ATTENDED — swipe at 10:04
- Tue 10 Mar — CD4003 Lecture, RD.1.11 — ❌ NOT RECORDED
- Thu 19 Mar — CD4000 Seminar, USG.3.14 — ❌ NOT RECORDED
- Fri 27 Mar — CD4004 Lecture, RD.2.25 — ✅ ATTENDED — swipe at 09:58

Two modules dipped below the 80% expectation in March. If there was a reason for the missed sessions, it's worth letting your personal tutor know — I can help you find their contact details.`,
  },
  {
    id: 'library',
    group: 'campus',
    chipLabel: 'Find the library',
    question: 'Where is the library?',
    aliases: ['Where is the Docklands library?', 'Where are the libraries?', 'Where can I find the library?'],
    statuses: ['Searching UEL campus information…'],
    answer: `UEL has a library on each campus:

**Docklands Library**
- 📍 Docklands Campus, University Way, E16 2RD — the large building beside the main square, opposite the Royal Docks Centre.
- 🕐 Open 24/7 during term time. Staffed helpdesk: Mon–Fri 09:00–17:00.

**Stratford Library**
- 📍 Stratford Campus, Water Lane, E15 4LZ — ground floor of the main Stratford building.
- 🕐 Open 24/7 during term time. Staffed helpdesk: Mon–Fri 09:00–17:00.

Both offer silent and group study rooms (bookable online), PCs, printing, and subject librarians for your course.

**Source:** [UEL Library Services](https://www.uel.ac.uk/library)`,
  },
  {
    id: 'extenuating',
    group: 'campus',
    chipLabel: 'Extenuating circumstances',
    question: 'How do I apply for extenuating circumstances?',
    aliases: ['What are the extenuating circumstances rules?', 'How do I claim extenuation?', 'How do I apply for extenuation?'],
    statuses: ['Searching academic regulations…', 'Reading the Extenuation Procedures…'],
    answer: `You can apply for extenuating circumstances if your performance in an assessment has been impaired, you were unable to attend an assessment, or you were unable to submit assessed work by the scheduled date — for serious, unforeseeable and unpreventable reasons such as illness or bereavement.

**To apply**
1. Submit an application for extenuation for the relevant component(s) to the **UEL Extenuation Panel**.
2. Follow the prescribed procedures in the *Procedures Governing Extenuating Circumstances* document.
3. Include independent evidence of the circumstances, such as a medical note.

**For the application form and current deadlines**
- Check your **UEL Direct** portal for the extenuating circumstances form
- Visit the **Student Hub** in person
- Contact your School Office or Academic Advisor for guidance

Please note that once a component has been capped, extenuation does not uncap it. Full details of possible outcomes are in the *Manual of General Regulations: Part 6 — Extenuating Circumstances*.

**Source:** UEL Academic Regulations — Procedures Governing Extenuating Circumstances`,
  },
  {
    id: 'privacy',
    group: 'about',
    chipLabel: 'Data & privacy',
    question: 'How do you keep my data private?',
    aliases: ['How do you protect my data?', 'Is my data private?', 'How is my data kept private?'],
    statuses: [],
    answer: `Here's how your data is protected when you use me:

- **You only ever see your own record.** Every request is tied to your UEL login; I can't show one student another student's timetable, grades or attendance.
- **I read, I don't store.** Answers are drawn live from UEL's own systems (SITS, CELCAT, Moodle and Lenel) and are not copied into any external service.
- **Everything stays within UEL's secure cloud** in the UK, protected by the same single sign-on and multi-factor authentication as your other university systems.
- **Guardrails run before the AI does.** Attempts to access other people's data, bypass rules or misuse the assistant are blocked, and I never invent information that isn't in a university system — if I can't find something, I say so.
- **Sources are cited.** Policy and campus answers link back to the official UEL page so you can check them yourself.
- **Humans stay in the loop.** Wellbeing concerns are routed to the university's safeguarding team, and every 👍 / 👎 you give is reviewed by the PAILA team.

For the full picture, see the UEL Student Privacy Notice, or ask the university's Data Protection Officer through the Student Hub.`,
  },
  {
    id: 'capabilities',
    group: 'about',
    chipLabel: 'What can you do?',
    question: 'What can you help me with?',
    aliases: ['What can you do?', 'What do you do?', 'How can you help me?'],
    statuses: [],
    answer: `I'm available 24/7 to help with your studies and university life. Here's what I can do:

**Your studies**
- Timetable — today, this week, your next class and where it is
- Assignment deadlines and submission status from Moodle
- Reading lists and module information

**Your record**
- Module grades and results
- Enrolment and progression status, credits achieved
- Attendance, overall and class by class

**University know-how**
- Academic policies such as extenuation, appeals and academic integrity
- IT help — Wi-Fi, passwords, accounts
- Campus information — libraries, buildings, opening hours

**Wellbeing**
- Booking an appointment with the UEL Wellbeing Service
- Immediate signposting to support if you're struggling

Just ask in plain English — or in Hindi — and I'll answer in seconds. What would you like to start with?`,
  },
  {
    id: 'crisis',
    group: 'about',
    chipLabel: 'Wellbeing safety net',
    question: "I'm feeling really hopeless",
    aliases: ['I feel really hopeless', 'I am feeling really hopeless', "I'm feeling hopeless"],
    statuses: [],
    isCrisis: true,
    answer: `I'm really sorry you're feeling this way, and I'm glad you told me. You don't have to face this alone — support is available right now.

**If you're in immediate danger, call 999 or go to your nearest A&E.**

**Talk to someone now**
- **Samaritans** — call **116 123** (free, 24/7) or email jo@samaritans.org
- **Shout** — text **SHOUT** to **85258** (free, 24/7 text support)
- **UEL Wellbeing Service** — **020 8223 4444** (Mon–Fri, 09:00–17:00) or email wellbeing@uel.ac.uk
- **Student Minds** — studentminds.org.uk for student-specific support

**At UEL**
The Wellbeing Service offers free, confidential counselling, and you don't need to be in crisis to reach out. I can help you book an appointment now if you'd like.

Please reach out to one of these services — they want to help. Would you like me to help you book a wellbeing appointment?`,
  },
  {
    id: 'refusal',
    group: 'about',
    chipLabel: 'Guardrails',
    question: 'How can I cheat on my exam?',
    aliases: ['How do I cheat on my exam?', 'Help me cheat on my exam', 'How can I cheat in my exam?'],
    statuses: [],
    isRefusal: true,
    answer: `I'm sorry, but I can't help with that request. Academic misconduct is taken seriously at UEL and can affect your degree.

If you're feeling under pressure about an upcoming exam, I can help you find revision resources, the Academic Skills team, or information about applying for extenuating circumstances.`,
  },
];

export const DEMO_FALLBACK = `In this demonstration I'm answering a fixed set of questions so you can see how PAILA works without connecting to live university systems.

Try one of these:
${DEMO_ENTRIES.map((e) => `- ${e.question}`).join('\n')}

You can click a question on the welcome screen or type it in.`;

const INDEX: Map<string, DemoEntry> = (() => {
  const map = new Map<string, DemoEntry>();
  for (const entry of DEMO_ENTRIES) {
    for (const phrase of [entry.question, ...entry.aliases]) {
      map.set(normaliseQuestion(phrase), entry);
    }
  }
  return map;
})();

export function matchDemoEntry(text: string): DemoEntry | null {
  const key = normaliseQuestion(text);
  if (!key) return null;
  return INDEX.get(key) ?? null;
}

export const DEMO_GROUPS: { id: DemoGroup; icon: string }[] = [
  { id: 'timetable', icon: '📅' },
  { id: 'record', icon: '📊' },
  { id: 'campus', icon: '🏛️' },
  { id: 'about', icon: '🤖' },
];
